import React, { useState, useEffect, useRef, useCallback } from "react";
import LessonForm from "./LessonForm";
import TemplateEditor from "./TemplateEditor";
import LessonPreview, { PreviewThemeToggle } from "./LessonPreview";
import SidebarTemplatePreview from "./SidebarTemplatePreview";
import CourseOverviewPanel from "./CourseOverviewPanel";
import CourseSettingsPanel from "./CourseSettingsPanel";
import AgentChatPanel from "./AgentChatPanel";
import { CourseCoverControls, CourseCoverPreview, syncCoverVariantsFromS3 } from "./CourseCoverWorkspace";
import {
  CourseDataAgent,
  CourseDataPreview,
  draftFromCourse,
  savePayloadFromDraft,
} from "./CourseDataWorkspace";
import { nanoid } from "nanoid";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import cloneDeep from "lodash/cloneDeep";
import api from "../api";
import CourseSelector from "./CourseSelector";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import { useAmazonContext } from "../../Contexts/AmazonContext";
import "./LessonBuilder.css";
import QuizSidebarTemplatePreview from "../quizBuilder/QuizSidebarTemplatePreview";
import QuizTemplateEditor from "../quizBuilder/QuizTemplateEditor";

// Highlights and notes are stored against a content block's id. Legacy blocks
// and freshly generated ones can arrive without one, and a block with no id
// gets a throwaway one on every read, so name them before they reach state.
const withAnimationIds = (blocks) =>
  (blocks || []).map((block) =>
    block?.id ? block : { ...block, id: nanoid() }
  );

const withQuizIds = (quizzes) =>
  (quizzes || []).map((q) => {
    const persistedId = q._id || undefined;
    return {
      ...q,
      ...(persistedId ? { _id: persistedId } : {}),
      id: persistedId || q.id || nanoid(),
    };
  });

// Course-wide AI edits land as per-lesson drafts until the user opens and
// saves each lesson. Only drafts with content survive a reload; "queued" and
// "editing" are in-flight states of this tab.
const COURSE_EDIT_CONCURRENCY = 3;

const draftsStorageKey = (course) =>
  course?.courseTitle ? `hh-lesson-drafts:${course.courseTitle}` : null;

const readStoredDrafts = (key) => {
  if (!key) return {};
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || "{}");
    const drafts = {};
    Object.entries(parsed || {}).forEach(([number, draft]) => {
      if (draft?.lesson) drafts[number] = { ...draft, status: "unsaved" };
    });
    return drafts;
  } catch (error) {
    console.warn("Could not read lesson drafts:", error);
    return {};
  }
};

const writeStoredDrafts = (key, drafts) => {
  if (!key) return;
  const keep = {};
  Object.entries(drafts || {}).forEach(([number, draft]) => {
    if (!draft?.lesson) return;
    keep[number] = {
      sectionTitle: draft.sectionTitle,
      lesson: draft.lesson,
      quizzes: draft.quizzes,
      updatedAt: draft.updatedAt,
    };
  });
  try {
    if (Object.keys(keep).length === 0) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(keep));
  } catch (error) {
    console.warn("Could not store lesson drafts (they are kept in memory for this tab):", error);
  }
};

const LessonEditor = () => {
  let { predefinedTemplates, setPredefinedTemplates, predefinedQuizTemplates, setPredefinedQuizTemplates, isDarkMode } =
    useAmazonContext();

  // ─── Selectors ────────────────────────────────────────────────────────────
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);

  // ─── Lesson state ─────────────────────────────────────────────────────────
  const [lesson, setLesson] = useState({
    sectionNumber: "",
    sectionTitle: "",
    sectionImage: "",
    topicHeaderNumber: "",
    topicHeader: "",
    topicNumber: "",
    topicTitle: "",
    lessonAnimation: [],
    lessonText: "",
  });
  const [quizzes, setQuizzes] = useState([]);
  const [originalLessonData, setOriginalLessonData] = useState(null);
  const [originalQuizIds, setOriginalQuizIds] = useState([]); // _id strings of quizzes at load time
  const [deletedQuizIds, setDeletedQuizIds] = useState([]);   // _id strings removed during this session
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const suppressUnsavedRef = useRef(false);
  const silentLoadIdRef = useRef(0);
  const [hydrateNonce, setHydrateNonce] = useState(0);
  const [lessonTextSource, setLessonTextSource] = useState(null);
  const [originalLessonTextSource, setOriginalLessonTextSource] = useState(null);

  // ─── Cross-lesson AI drafts ───────────────────────────────────────────────
  // sectionNumber -> { status: "queued" | "editing" | "unsaved" | "error", lesson?, quizzes?, sectionTitle, message?, updatedAt? }
  const [lessonDrafts, setLessonDrafts] = useState({});
  const lessonDraftsRef = useRef({});
  const draftsKeyRef = useRef(null);
  const courseEditRunIdsRef = useRef(new Set());
  const courseEditCancelledRef = useRef(false);
  const lessonRef = useRef(null);
  const quizzesRef = useRef([]);
  lessonRef.current = lesson;
  quizzesRef.current = quizzes;

  const updateLessonDrafts = useCallback((updater) => {
    const next = updater(lessonDraftsRef.current);
    lessonDraftsRef.current = next;
    setLessonDrafts(next);
    writeStoredDrafts(draftsKeyRef.current, next);
  }, []);

  const removeLessonDraft = useCallback(
    (sectionNumber) => {
      const key = String(sectionNumber || "");
      if (!key || !lessonDraftsRef.current[key]) return;
      updateLessonDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [updateLessonDrafts]
  );

  // ─── Chapter / topic headers for course ───────────────────────────────────
  const [chapters, setChapters] = useState([]);

  // ─── All lessons (flat) for course overview + agent context ───────────────
  const [allLessons, setAllLessons] = useState([]);

  // ─── Panels ───────────────────────────────────────────────────────────────
  const [isOverviewOpen, setIsOverviewOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isCourseSettingsOpen, setIsCourseSettingsOpen] = useState(false);
  const [editorView, setEditorView] = useState("lesson"); // "lesson" | "cover" | "courseData"

  // ─── Course cover workspace ───────────────────────────────────────────────
  const [coverConcept, setCoverConcept] = useState("");
  const [coverExtraGuidance, setCoverExtraGuidance] = useState("");
  const [coverOptions, setCoverOptions] = useState([]);
  const [selectedCoverIndex, setSelectedCoverIndex] = useState(0);
  const [coverBusy, setCoverBusy] = useState(null);
  const [coverError, setCoverError] = useState(null);
  const [coverCacheBust, setCoverCacheBust] = useState(0);

  // ─── Course data workspace ────────────────────────────────────────────────
  const [courseDraft, setCourseDraft] = useState(null);
  const [courseDraftOriginal, setCourseDraftOriginal] = useState(null);
  const [hasCourseDataUnsaved, setHasCourseDataUnsaved] = useState(false);
  const [courseDataSaving, setCourseDataSaving] = useState(false);
  const [courseDataSaveError, setCourseDataSaveError] = useState(null);
  const [courseDataSaveNotice, setCourseDataSaveNotice] = useState(null);

  // ─── Preview ──────────────────────────────────────────────────────────────
  const [previewMode, setPreviewMode] = useState(true);
  const [previewDark, setPreviewDark] = useState(isDarkMode);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(-1);

  // ─── Template editing ─────────────────────────────────────────────────────
  const [activeTemplateIndex, setActiveTemplateIndex] = useState(null);

  // ─── Saving ───────────────────────────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ step: "", attempt: 0, maxAttempts: 0 });

  // ─── JSON export (secondary) ──────────────────────────────────────────────
  const [jsonOutput, setJsonOutput] = useState("");

  // ─── AI image generation ──────────────────────────────────────────────────
  const [imageTheme, setImageTheme] = useState(
    "minimalist illustration of {description}, vector art in the style of comic book, flat colors, simple shapes, whimsical motive, dreamy"
  );
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [imageGenerationProgress, setImageGenerationProgress] = useState("");
  const [imageGenerationError, setImageGenerationError] = useState(null);
  const [selectedSectionImageTemplateId, setSelectedSectionImageTemplateId] = useState(null);

  // ─── Contextual regeneration modal ────────────────────────────────────────
  const [regenModal, setRegenModal] = useState({ isOpen: false, lessonText: "" });
  const [isRegenerating, setIsRegenerating] = useState(false);

  // ─── Parallel regeneration tracking ──────────────────────────────────────
  // Map: sectionNumber -> { status: "generating" | "done" | "error", result? }
  const [regeneratingLessons, setRegeneratingLessons] = useState(new Map());
  const [regenToast, setRegenToast] = useState(null);

  // ─── Mobile tab ───────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("edit"); // "overview" | "edit" | "chat" | "preview"

  // ─── Fetch course data ────────────────────────────────────────────────────
  const fetchChapters = useCallback(async () => {
    if (!selectedCourse) return;
    try {
      const response = await api.get(`/${selectedCourse.courseTitle}/topicsData`, {
        params: {
          modelName: selectedCourse.courseTopicsModelName,
          collectionName: selectedCourse.courseTopicsCollectionName,
        },
      });
      setChapters(response.data);
    } catch (error) {
      console.error("Error fetching chapters:", error);
    }
  }, [selectedCourse]);

  const fetchAllLessons = useCallback(async () => {
    if (!selectedCourse) return;
    try {
      const response = await api.get(`/${selectedCourse.courseTitle}/allLessons`, {
        params: {
          modelName: selectedCourse.courseTopicsModelName,
          collectionName: selectedCourse.courseTopicsCollectionName,
        },
      });
      setAllLessons(response.data || []);
    } catch (error) {
      console.error("Error fetching all lessons:", error);
    }
  }, [selectedCourse]);

  useEffect(() => {
    if (selectedCourse) {
      fetchChapters();
      fetchAllLessons();
    }
  }, [selectedCourse]);

  useEffect(() => {
    setEditorView("lesson");
    setCoverOptions([]);
    setSelectedCoverIndex(0);
    setCoverConcept("");
    setCoverExtraGuidance("");
    setCoverError(null);
    setCoverCacheBust(Date.now());
    setCourseDraft(null);
    setCourseDraftOriginal(null);
    setHasCourseDataUnsaved(false);
    setCourseDataSaveError(null);
    setCourseDataSaveNotice(null);

    courseEditCancelledRef.current = true;
    draftsKeyRef.current = draftsStorageKey(selectedCourse);
    const stored = readStoredDrafts(draftsKeyRef.current);
    lessonDraftsRef.current = stored;
    setLessonDrafts(stored);
  }, [selectedCourse?.courseTitle]);

  // ─── Load templates ───────────────────────────────────────────────────────
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await api.get("/fetchTemplates");
        setPredefinedTemplates(response.data);
      } catch (error) {
        console.error("Error fetching templates:", error);
      }
    };

    const loadQuizTemplates = () => {
      setPredefinedQuizTemplates([
        {
          name: "Multi choice - one answer",
          question: "What is the fundamental reason Allah sent the Messengers?",
          answers: [
            { answer: "Tawheed – to teach the people to worship Allah Alone", isCorrect: true },
            { answer: "To perfect the character of the people", explanation: "This is one of the reasons but not the fundamental one.", isCorrect: false },
            { answer: "To teach the people to follow the 5 pillars of Islam", explanation: "This is not the fundamental reason", isCorrect: false },
            { answer: "To give righteous glad tidings of Paradise and to give the evil doers the news of the Hell Fire.", explanation: "Yes this is one of the reasons but not the fundamental reason.", isCorrect: false },
          ],
        },
      ]);
    };

    if (!predefinedTemplates || predefinedTemplates.length === 0) fetchTemplates();
    if (!predefinedQuizTemplates || predefinedQuizTemplates.length === 0) loadQuizTemplates();
  }, [predefinedTemplates, setPredefinedTemplates, predefinedQuizTemplates, setPredefinedQuizTemplates]);

  // ─── Load lesson when selected ────────────────────────────────────────────
  useEffect(() => {
    if (selectedLesson && selectedCourse) {
      loadLessonData();
    }
  }, [selectedLesson, selectedCourse]);

  // Captures the segment after AI-Generated-Lessons/, which is the lesson id
  // even when the URL is prefixed with a course folder.
  const extractLessonIdFromImages = (lessonAnimation) => {
    if (!lessonAnimation || !Array.isArray(lessonAnimation)) return null;
    for (const section of lessonAnimation) {
      if (!section.subSections) continue;
      for (const sub of section.subSections) {
        const url = sub.imageFilePath;
        if (url) {
          const match = url.match(/AI-Generated-Lessons\/([^/]+)\//);
          if (match) return match[1];
        }
        if (sub.miniSections) {
          for (const mini of sub.miniSections) {
            const miniUrl = mini.imageFilePath;
            if (miniUrl) {
              const match = miniUrl.match(/AI-Generated-Lessons\/([^/]+)\//);
              if (match) return match[1];
            }
          }
        }
      }
    }
    return null;
  };

  const beginSilentLoad = () => {
    suppressUnsavedRef.current = true;
    silentLoadIdRef.current += 1;
    return silentLoadIdRef.current;
  };

  const finishSilentLoad = (loadId) => {
    if (loadId != null && loadId !== silentLoadIdRef.current) return;
    setHasUnsavedChanges(false);
    setHydrateNonce((n) => n + 1);
  };

  const loadLessonData = async () => {
    const loadId = beginSilentLoad();
    let appliedDraft = false;
    try {
      const response = await api.get(
        `/${selectedCourse.courseTitle.replace(/\s/g, "")}/lessonRESTCAll/${selectedLesson.sectionNumber}?collectionName=${selectedCourse.courseTopicsCollectionName}&modelName=${selectedCourse.courseTopicsModelName}`
      );

      if (response.data?.sectionNumber) {
        const lessonData = response.data;
        setOriginalLessonData(lessonData);

        const existingLessonId = extractLessonIdFromImages(lessonData.lessonAnimation);

        const chapterForLesson =
          selectedChapter &&
          (selectedChapter.sections || []).some(
            (s) => String(s.sectionNumber) === String(lessonData.sectionNumber)
          )
            ? selectedChapter
            : (chapters || []).find((ch) =>
                (ch.sections || []).some(
                  (s) => String(s.sectionNumber) === String(lessonData.sectionNumber)
                )
              );

        setLesson({
          sectionNumber: lessonData.sectionNumber || "",
          sectionTitle: lessonData.sectionTitle || "",
          sectionImage: lessonData.sectionImage || "",
          topicHeaderNumber: lessonData.topicHeaderNumber || chapterForLesson?.topicHeaderNumber || "",
          topicHeader: lessonData.topicHeader || chapterForLesson?.topicHeader || "",
          topicNumber: lessonData.topicNumber || chapterForLesson?.topicNumber || "",
          topicTitle: lessonData.topicTitle || chapterForLesson?.topicTitle || "",
          lessonAnimation: withAnimationIds(lessonData.lessonAnimation),
          lessonText: "",
          ...(existingLessonId ? { lessonId: existingLessonId } : {}),
        });

        setLessonTextSource(null);
        setOriginalLessonTextSource(null);

        // Always fetch quizzes by sectionNumber — don't rely on exampleId being set
        const loadedQuizIds = await loadLessonQuizzes(lessonData.sectionNumber);
        await loadLessonSourceText(lessonData.sectionNumber);

        const draft = lessonDraftsRef.current[String(lessonData.sectionNumber)];
        if (draft?.lesson && loadId === silentLoadIdRef.current) {
          setLesson((prev) => ({
            ...prev,
            sectionTitle: draft.lesson.sectionTitle ?? prev.sectionTitle,
            sectionImage: draft.lesson.sectionImage ?? prev.sectionImage,
            lessonId: draft.lesson.lessonId || prev.lessonId,
            lessonAnimation: withAnimationIds(draft.lesson.lessonAnimation),
          }));
          const draftQuizzes = withQuizIds(draft.quizzes);
          setQuizzes(draftQuizzes);
          const kept = new Set(draftQuizzes.map((q) => String(q._id || "")));
          setDeletedQuizIds(loadedQuizIds.filter((id) => !kept.has(String(id))));
          appliedDraft = true;
        } else {
          setDeletedQuizIds([]);
        }
        setIsEditMode(true);
      }
    } catch (error) {
      console.error("Error loading lesson data:", error);
    } finally {
      finishSilentLoad(loadId);
      if (appliedDraft && loadId === silentLoadIdRef.current) setHasUnsavedChanges(true);
    }
  };

  const loadLessonSourceText = async (sectionNumber) => {
    if (!selectedCourse || !sectionNumber) {
      setLessonTextSource(null);
      setOriginalLessonTextSource(null);
      return;
    }
    try {
      const { data } = await api.get("/api/course-import/source-text", {
        params: {
          courseId: selectedCourse._id,
          courseSlug: selectedCourse.courseTitle
            ? selectedCourse.courseTitle.replace(/\s/g, "")
            : "",
          sectionNumber,
          collectionName: selectedCourse.courseTopicsCollectionName,
        },
      });
      const text = data?.lessonText || "";
      const source = data?.source || null;
      setLesson((prev) => ({ ...prev, lessonText: text }));
      setLessonTextSource(source);
      setOriginalLessonTextSource(source);
      setOriginalLessonData((prev) => (prev ? { ...prev, lessonText: text } : prev));
    } catch (error) {
      console.error("Error loading lesson source text:", error);
    }
  };

  const loadLessonQuizzes = async (sectionNumber) => {
    if (!selectedCourse?.courseExamplesModelName || !selectedCourse?.courseExamplesCollectionName) {
      setQuizzes([]);
      setOriginalQuizIds([]);
      return [];
    }
    try {
      const response = await api.get(`/examplesData`, {
        params: {
          modelName: selectedCourse.courseExamplesModelName,
          collectionName: selectedCourse.courseExamplesCollectionName,
        },
      });
      if (response.data && Array.isArray(response.data)) {
        const sectionStr = String(sectionNumber);
        const filtered = response.data.filter(
          (q) => String(q.sectionNumber) === sectionStr
        );
        if (filtered.length > 0) {
          const quizzesWithIds = filtered.map((q) => ({
            ...q,
            id: q._id || nanoid(),
          }));
          const ids = quizzesWithIds.map((q) => q._id).filter(Boolean);
          setQuizzes(quizzesWithIds);
          setOriginalQuizIds(ids);
          return ids;
        }
        setQuizzes([]);
        setOriginalQuizIds([]);
      } else {
        setQuizzes([]);
        setOriginalQuizIds([]);
      }
    } catch (error) {
      console.error("Error loading lesson quizzes:", error);
      setQuizzes([]);
      setOriginalQuizIds([]);
    }
    return [];
  };

  // Mark unsaved whenever lesson/quiz changes after initial load
  useEffect(() => {
    if (suppressUnsavedRef.current) return;
    if (isEditMode) {
      setHasUnsavedChanges(true);
    }
  }, [lesson.lessonAnimation, quizzes]);

  useEffect(() => {
    suppressUnsavedRef.current = false;
  }, [hydrateNonce]);

  // Keep the open lesson's draft in step with editor edits, so switching away
  // from a drafted lesson does not lose what was changed on top of it.
  useEffect(() => {
    const key = String(lesson.sectionNumber || "");
    if (!key || !hasUnsavedChanges || suppressUnsavedRef.current) return undefined;
    if (lessonDraftsRef.current[key]?.status !== "unsaved") return undefined;
    const timer = window.setTimeout(() => {
      if (suppressUnsavedRef.current) return;
      const { lessonText, ...current } = lessonRef.current;
      if (String(current.sectionNumber) !== key) return;
      updateLessonDrafts((prev) =>
        prev[key]?.status === "unsaved"
          ? {
              ...prev,
              [key]: {
                ...prev[key],
                sectionTitle: current.sectionTitle,
                lesson: current,
                quizzes: quizzesRef.current,
                updatedAt: Date.now(),
              },
            }
          : prev
      );
    }, 500);
    return () => window.clearTimeout(timer);
  }, [lesson, quizzes, hasUnsavedChanges, updateLessonDrafts]);

  // ─── Template management ──────────────────────────────────────────────────
  const addTemplate = (template) => {
    const newTemplate = cloneDeep(template);
    newTemplate.id = nanoid();
    setLesson((prev) => ({ ...prev, lessonAnimation: [...prev.lessonAnimation, newTemplate] }));
  };

  const addQuiz = (quiz) => {
    const newQuiz = cloneDeep(quiz);
    newQuiz.id = nanoid();
    newQuiz.sectionNumber = lesson.sectionNumber;
    setQuizzes((prev) => [...prev, newQuiz]);
  };

  const updateTemplate = (index, updatedTemplate) => {
    setLesson((prev) => {
      const newAnim = [...prev.lessonAnimation];
      newAnim[index] = updatedTemplate;
      return { ...prev, lessonAnimation: newAnim };
    });
  };

  const deleteTemplate = (index) => {
    setLesson((prev) => {
      const newAnim = [...prev.lessonAnimation];
      newAnim.splice(index, 1);
      return { ...prev, lessonAnimation: newAnim };
    });
  };

  const duplicateTemplate = (index, template) => {
    const dup = cloneDeep(template);
    dup.id = nanoid();
    setLesson((prev) => {
      const newAnim = [...prev.lessonAnimation];
      newAnim.splice(index + 1, 0, dup);
      return { ...prev, lessonAnimation: newAnim };
    });
  };

  const updateQuiz = (index, updatedQuiz) => {
    setQuizzes((prev) => {
      const next = [...prev];
      next[index] = updatedQuiz;
      return next;
    });
  };

  const deleteQuiz = (index) => {
    setQuizzes((prev) => {
      const next = [...prev];
      const removed = next.splice(index, 1)[0];
      if (removed?._id) {
        setDeletedQuizIds((d) => [...d, removed._id]);
      }
      return next;
    });
  };

  const duplicateQuiz = (index, quiz) => {
    const dup = cloneDeep(quiz);
    dup.id = nanoid();
    delete dup._id;
    setQuizzes((prev) => {
      const next = [...prev];
      next.splice(index + 1, 0, dup);
      return next;
    });
  };

  // ─── Agent callbacks ──────────────────────────────────────────────────────
  const handleAgentLessonUpdate = (updatedLesson) => {
    setLesson((prev) => ({
      ...prev,
      sectionTitle: updatedLesson.sectionTitle ?? prev.sectionTitle,
      topicTitle: updatedLesson.topicTitle ?? prev.topicTitle,
      sectionImage: updatedLesson.sectionImage ?? prev.sectionImage,
      lessonId: updatedLesson.lessonId || prev.lessonId,
      lessonAnimation: updatedLesson.lessonAnimation
        ? withAnimationIds(updatedLesson.lessonAnimation)
        : prev.lessonAnimation,
    }));
    setHasUnsavedChanges(true);
  };

  const handleAgentQuizzesUpdate = (updatedQuizzes) => {
    setQuizzes(withQuizIds(updatedQuizzes));
    setHasUnsavedChanges(true);
  };

  // ─── Course-wide AI edit ──────────────────────────────────────────────────
  // Runs the confirmed instruction once per target lesson. The open lesson is
  // edited in place; every other lesson becomes an unsaved draft.
  const runCourseEdit = async (plan, onProgress) => {
    const targets = plan?.targets || [];
    const courseKey = draftsKeyRef.current;
    if (!selectedCourse || targets.length === 0) {
      return { edited: 0, unchanged: 0, failed: 0, skipped: 0, total: 0 };
    }

    courseEditCancelledRef.current = false;
    const tally = { edited: 0, unchanged: 0, failed: 0, skipped: 0, total: targets.length };
    const report = () => onProgress && onProgress({ ...tally });
    const stillThisCourse = () => draftsKeyRef.current === courseKey;

    const setStatus = (number, patch) =>
      updateLessonDrafts((prev) => ({
        ...prev,
        [number]: { ...prev[number], sectionTitle: prev[number]?.sectionTitle, ...patch },
      }));

    // A lesson's prior draft (if any) is what the next run builds on.
    const restorePrior = (number) =>
      updateLessonDrafts((prev) => {
        const next = { ...prev };
        if (next[number]?.lesson) next[number] = { ...next[number], status: "unsaved", message: undefined };
        else delete next[number];
        return next;
      });

    targets.forEach((t) =>
      setStatus(String(t.sectionNumber), {
        status: "queued",
        sectionTitle: t.sectionTitle,
        message: undefined,
      })
    );

    const queue = targets.map((t) => String(t.sectionNumber));

    const runOne = async (number) => {
      const isOpen = () => String(lessonRef.current?.sectionNumber || "") === number;
      const prior = lessonDraftsRef.current[number];
      const sendOpen = isOpen();
      const runId = `course-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      courseEditRunIdsRef.current.add(runId);
      setStatus(number, { status: "editing" });

      let body;
      if (sendOpen) {
        body = { lesson: lessonRef.current, quizzes: quizzesRef.current };
      } else if (prior?.lesson) {
        body = { lesson: prior.lesson, quizzes: prior.quizzes || [] };
      } else {
        body = { targetSectionNumber: number };
      }

      try {
        const response = await api.post("/api/chat-lesson-edit", {
          ...body,
          runId,
          batchMode: true,
          userMessage: plan.instruction,
          chatHistory: [],
          imageTheme,
          courseTitle: selectedCourse.courseTitle,
          modelName: selectedCourse.courseTopicsModelName,
          collectionName: selectedCourse.courseTopicsCollectionName,
          quizModelName: selectedCourse.courseExamplesModelName,
          quizCollectionName: selectedCourse.courseExamplesCollectionName,
        });
        if (!stillThisCourse()) return;
        const { updatedLesson, updatedQuizzes, changed, assistantMessage } = response.data || {};
        if (!changed || !updatedLesson) {
          tally.unchanged += 1;
          restorePrior(number);
          return;
        }

        const { lessonText, ...draftLesson } = updatedLesson;
        if (isOpen()) {
          handleAgentLessonUpdate(updatedLesson);
          handleAgentQuizzesUpdate(updatedQuizzes);
        }
        setStatus(number, {
          status: "unsaved",
          sectionTitle: draftLesson.sectionTitle,
          lesson: draftLesson,
          quizzes: updatedQuizzes || [],
          message: assistantMessage,
          updatedAt: Date.now(),
        });
        tally.edited += 1;
      } catch (err) {
        if (!stillThisCourse()) return;
        const message = err.response?.data?.error || err.message || "Edit failed.";
        tally.failed += 1;
        setStatus(number, { status: "error", message });
      } finally {
        courseEditRunIdsRef.current.delete(runId);
        report();
      }
    };

    const worker = async () => {
      while (queue.length > 0) {
        const number = queue.shift();
        if (courseEditCancelledRef.current || !stillThisCourse()) {
          tally.skipped += 1;
          if (stillThisCourse()) restorePrior(number);
          report();
          continue;
        }
        await runOne(number);
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(COURSE_EDIT_CONCURRENCY, queue.length) }, worker)
    );
    return { ...tally };
  };

  const stopCourseEdit = async () => {
    courseEditCancelledRef.current = true;
    const runIds = Array.from(courseEditRunIdsRef.current);
    await Promise.all(
      runIds.map((runId) =>
        api.post("/api/chat-lesson-edit/stop", { runId }).catch(() => null)
      )
    );
  };

  // Drafts are keyed by lesson number, so a renumber or delete must carry them
  // along or they would open on top of a different lesson.
  const remapLessonDrafts = (plan) => {
    const deleted = new Set((plan?.deletes?.lessons || []).map((d) => String(d.sectionNumber)));
    const moves = new Map((plan?.lessonUpdates || []).map((u) => [String(u.from), String(u.to)]));
    if (deleted.size === 0 && moves.size === 0) return;
    updateLessonDrafts((prev) => {
      const next = {};
      Object.entries(prev).forEach(([number, draft]) => {
        if (deleted.has(number)) return;
        const to = moves.get(number) || number;
        next[to] =
          to === number || !draft.lesson
            ? draft
            : {
                ...draft,
                lesson: { ...draft.lesson, sectionNumber: to },
                quizzes: (draft.quizzes || []).map((q) => ({ ...q, sectionNumber: to })),
              };
      });
      return next;
    });
  };

  const handleRenumberApplied = async (plan) => {
    if (!plan?.move || !selectedCourse) return;
    const move = plan.move;
    remapLessonDrafts(plan);
    try {
      const response = await api.get(`/${selectedCourse.courseTitle}/topicsData`, {
        params: {
          modelName: selectedCourse.courseTopicsModelName,
          collectionName: selectedCourse.courseTopicsCollectionName,
        },
      });
      setChapters(response.data);
      const chapter = (response.data || []).find(
        (ch) => String(ch.topicNumber) === String(move.toTopicNumber)
      );
      if (chapter) setSelectedChapter(chapter);
    } catch (error) {
      console.error("Error refreshing chapters after renumber:", error);
    }

    setSelectedLesson({ sectionNumber: move.newSectionNumber });
    setHasUnsavedChanges(false);
    fetchAllLessons();
  };

  const handleLabelRenameApplied = async (plan) => {
    if (!plan?.ok || !selectedCourse) return;
    const updates = plan.updates || {};
    setLesson((prev) => ({
      ...prev,
      ...(updates.sectionTitle ? { sectionTitle: updates.sectionTitle } : {}),
      ...(updates.topicTitle ? { topicTitle: updates.topicTitle } : {}),
      ...(updates.topicHeader ? { topicHeader: updates.topicHeader } : {}),
    }));
    setOriginalLessonData((prev) =>
      prev
        ? {
            ...prev,
            ...(updates.sectionTitle ? { sectionTitle: updates.sectionTitle } : {}),
            ...(updates.topicTitle ? { topicTitle: updates.topicTitle } : {}),
            ...(updates.topicHeader ? { topicHeader: updates.topicHeader } : {}),
          }
        : prev
    );
    try {
      const response = await api.get(`/${selectedCourse.courseTitle}/topicsData`, {
        params: {
          modelName: selectedCourse.courseTopicsModelName,
          collectionName: selectedCourse.courseTopicsCollectionName,
        },
      });
      setChapters(response.data);
      const topicNumber = updates.topicNumber || lesson.topicNumber;
      const chapter = (response.data || []).find(
        (ch) => String(ch.topicNumber) === String(topicNumber)
      );
      if (chapter) setSelectedChapter(chapter);
    } catch (error) {
      console.error("Error refreshing chapters after label rename:", error);
    }
    fetchAllLessons();
  };

  const handleDeleteApplied = async (plan) => {
    if (!plan?.ok || !selectedCourse) return;
    remapLessonDrafts(plan);
    const nextNumber = plan.openLessonAfter;
    const current = lesson?.sectionNumber;
    const numberChanged = nextNumber && String(nextNumber) !== String(current);
    try {
      const response = await api.get(`/${selectedCourse.courseTitle}/topicsData`, {
        params: {
          modelName: selectedCourse.courseTopicsModelName,
          collectionName: selectedCourse.courseTopicsCollectionName,
        },
      });
      setChapters(response.data);
      const matchNumber = plan.openLessonDeleted || numberChanged ? nextNumber : current;
      const chapter = (response.data || []).find((ch) =>
        (ch.sections || []).some((s) => String(s.sectionNumber) === String(matchNumber))
      );
      if (chapter) setSelectedChapter(chapter);
      if (plan.openLessonDeleted || numberChanged) {
        if (nextNumber) setSelectedLesson({ sectionNumber: nextNumber });
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error("Error refreshing chapters after delete:", error);
    }
    fetchAllLessons();
  };

  // ─── Course overview — select lesson from panel ───────────────────────────
  const handleSelectLessonFromOverview = (lessonMeta) => {
    const openKey = String(lesson.sectionNumber || "");
    if (hasUnsavedChanges && lessonDraftsRef.current[openKey]?.status === "unsaved") {
      const { lessonText, ...current } = lesson;
      updateLessonDrafts((prev) => ({
        ...prev,
        [openKey]: {
          ...prev[openKey],
          sectionTitle: current.sectionTitle,
          lesson: current,
          quizzes,
          updatedAt: Date.now(),
        },
      }));
    }
    // Find the chapter that contains this section
    const chapter = chapters.find((ch) =>
      (ch.sections || []).some((s) => s.sectionNumber === lessonMeta.sectionNumber)
    );
    if (chapter) {
      setSelectedChapter(chapter);
    }
    // Set selectedLesson to trigger loadLessonData via useEffect
    setSelectedLesson({ sectionNumber: lessonMeta.sectionNumber });
    setEditorView("lesson");
    setIsOverviewOpen(false);
    setActiveTab("edit");
  };

  const loadAdminCourse = useCallback(async () => {
    if (!selectedCourse) return null;
    const response = await api.get("/api/admin/courses");
    return (
      response.data.find((item) => String(item._id) === String(selectedCourse._id)) ||
      response.data.find((item) => item.courseTitle === selectedCourse.courseTitle) ||
      selectedCourse
    );
  }, [selectedCourse]);

  const loadPendingCovers = useCallback(async (courseId) => {
    if (!courseId) return;
    try {
      const response = await api.get(`/api/admin/courses/${courseId}/cover`);
      const listed = response.data.variants || [];
      setCoverOptions((prev) => syncCoverVariantsFromS3(prev, listed));
    } catch (error) {
      console.error("Error loading pending covers:", error);
    }
  }, []);

  useEffect(() => {
    const courseId = selectedCourse?._id;
    if (!courseId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get(`/api/admin/courses/${courseId}/cover`);
        if (cancelled) return;
        const listed = response.data.variants || [];
        setCoverOptions((prev) => syncCoverVariantsFromS3(prev, listed));
      } catch (error) {
        if (!cancelled) console.error("Error loading pending covers:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCourse?._id]);

  const handleSelectWorkspace = async (view) => {
    setEditorView(view);
    setIsOverviewOpen(false);
    setActiveTab("edit");
    if (!selectedCourse) return;
    try {
      const match = await loadAdminCourse();
      if (!match) return;
      setSelectedCourse((prev) => {
        if (!prev) return match;
        return {
          ...prev,
          ...match,
          courseTitle: prev.courseTitle,
          _id: match._id || prev._id,
        };
      });
      if (view === "cover") {
        setCoverConcept((prev) => prev || match.courseDescription || courseDraft?.coverConcept || "");
        if (match._id) await loadPendingCovers(match._id);
      }
      if (view === "courseData") {
        const keepExisting = courseDraft && String(courseDraft._id) === String(match._id);
        if (!keepExisting) {
          const draft = draftFromCourse(match);
          setCourseDraft(draft);
          setCourseDraftOriginal(JSON.stringify(savePayloadFromDraft(draft)));
          setHasCourseDataUnsaved(false);
        }
      }
    } catch (error) {
      console.error("Error loading course for workspace:", error);
    }
  };

  const handleCourseDraftUpdate = (updated) => {
    const next = draftFromCourse({ ...courseDraft, ...updated });
    setCourseDraft(next);
    setHasCourseDataUnsaved(
      JSON.stringify(savePayloadFromDraft(next)) !== courseDraftOriginal
    );
    if (updated.coverConcept) setCoverConcept(updated.coverConcept);
  };

  const handleSaveCourseData = async () => {
    if (!courseDraft?._id) return;
    setCourseDataSaving(true);
    setCourseDataSaveError(null);
    try {
      const response = await api.put(
        `/api/admin/courses/${courseDraft._id}`,
        savePayloadFromDraft(courseDraft)
      );
      const saved = draftFromCourse({ ...courseDraft, ...response.data });
      setCourseDraft(saved);
      setCourseDraftOriginal(JSON.stringify(savePayloadFromDraft(saved)));
      setHasCourseDataUnsaved(false);
      setSelectedCourse((prev) =>
        prev ? { ...prev, ...response.data, courseTitle: prev.courseTitle } : response.data
      );
      setCourseDataSaveNotice("Saved.");
      setTimeout(() => setCourseDataSaveNotice(null), 3000);
    } catch (error) {
      setCourseDataSaveError(
        error?.response?.data?.error || error.message || "Failed to save course."
      );
    } finally {
      setCourseDataSaving(false);
    }
  };

  // ─── Save to DB ───────────────────────────────────────────────────────────
  const saveLesson = async () => {
    if (!selectedCourse || !selectedChapter?._id) {
      alert("Course and chapter must be selected to save.");
      return;
    }

    const unnamedBlocks = (lesson.lessonAnimation || [])
      .map((block, index) => (block?.id ? null : index + 1))
      .filter(Boolean);
    if (unnamedBlocks.length > 0) {
      alert(
        `Cannot save lesson. Missing unique identifier on content item(s): ${unnamedBlocks.join(", ")}`
      );
      return;
    }

    setIsSaving(true);
    setSaveProgress({ step: "Saving lesson…", attempt: 1, maxAttempts: 3 });

    try {
      const cleanedLesson = buildCleanLessonForExport();
      const courseUrlTitle = selectedCourse.courseTitle.replace(/\s/g, "");
      const lookupNumber =
        originalLessonData?.sectionNumber || cleanedLesson.sectionNumber;

      // 1. Save lesson to DB
      setSaveProgress({ step: "Saving lesson content…", attempt: 1, maxAttempts: 3 });
      await api.put(`/${courseUrlTitle}/updateLesson`, {
        modelName: selectedCourse.courseTopicsModelName,
        collectionName: selectedCourse.courseTopicsCollectionName,
        chapterId: selectedChapter._id,
        lessonNumber: lookupNumber,
        updatedLesson: cleanedLesson,
      });

      // 2. Delete removed quizzes
      if (deletedQuizIds.length > 0) {
        setSaveProgress({ step: "Removing deleted quizzes…", attempt: 2, maxAttempts: 3 });
        await Promise.all(
          deletedQuizIds.map((id) =>
            api.delete(
              `/${courseUrlTitle}/deleteQuiz/${id}?modelName=${selectedCourse.courseExamplesModelName}&collectionName=${selectedCourse.courseExamplesCollectionName}`
            )
          )
        );
        setDeletedQuizIds([]);
      }

      // 3. Upsert quizzes
      setSaveProgress({ step: "Saving quizzes…", attempt: 3, maxAttempts: 3 });
      const originalSet = new Set(originalQuizIds.map(String));
      const existingQuizzes = quizzes.filter(
        (q) => q._id && originalSet.has(String(q._id))
      );
      const newQuizzes = quizzes.filter(
        (q) => !q._id || !originalSet.has(String(q._id))
      );

      await Promise.all(
        existingQuizzes.map((q) => {
          const { id, ...rest } = q;
          return api.put(
            `/${courseUrlTitle}/updateQuiz/${q._id}`,
            {
              modelName: selectedCourse.courseExamplesModelName,
              collectionName: selectedCourse.courseExamplesCollectionName,
              updatedQuiz: rest,
            }
          );
        })
      );

      if (newQuizzes.length > 0) {
        const cleaned = newQuizzes.map(({ id, _id, ...rest }) => ({
          ...rest,
          sectionNumber: lesson.sectionNumber,
        }));
        await api.post(`/${courseUrlTitle}/addQuizzes`, {
          modelName: selectedCourse.courseExamplesModelName,
          collectionName: selectedCourse.courseExamplesCollectionName,
          quizzes: cleaned,
        });
      }

      setHasUnsavedChanges(false);
      removeLessonDraft(lesson.sectionNumber);
      if (String(lesson.lessonText || "").trim()) {
        setLessonTextSource("section");
        setOriginalLessonTextSource("section");
        setOriginalLessonData((prev) =>
          prev ? { ...prev, lessonText: lesson.lessonText } : prev
        );
      }
      fetchAllLessons();

      const hasUnusedImageOptions = (lesson.lessonAnimation || []).some((section) =>
        section.subSections?.some(
          (sub) =>
            (sub.imageOptions && sub.imageOptions.length > 0) ||
            (sub.miniSections &&
              sub.miniSections.some((mini) => mini.imageOptions && mini.imageOptions.length > 0))
        )
      );

      let successMessage = "Saved successfully!";
      if (hasUnusedImageOptions && lesson.lessonId) {
        const confirmed = window.confirm(
          "Saved to the database.\n\nUnused image options will be deleted from AWS. Only your selected images will be kept.\n\nProceed?"
        );
        if (confirmed) {
          setSaveProgress({
            step: "Cleaning up unused images from AWS…",
            attempt: 3,
            maxAttempts: 3,
          });
          try {
            const cleanupResponse = await api.post("/api/cleanup-unused-images", {
              lessonAnimation: lesson.lessonAnimation,
              lessonId: lesson.lessonId,
              courseTitle: selectedCourse?.courseTitle,
            });
            if (cleanupResponse.data.success) {
              // Matches what was just saved, so it must not count as an unsaved edit.
              const loadId = beginSilentLoad();
              setLesson((prev) => ({
                ...prev,
                lessonAnimation: cleanedLesson.lessonAnimation,
              }));
              finishSilentLoad(loadId);
              successMessage = `Saved successfully! Cleaned up ${cleanupResponse.data.deletedCount} unused images from AWS.`;
            } else {
              successMessage = "Saved successfully! Unused images could not be deleted from AWS.";
            }
          } catch (cleanupErr) {
            console.error("Error cleaning up unused images after save:", cleanupErr);
            successMessage = "Saved successfully! Unused images could not be deleted from AWS.";
          }
        }
      }

      setSaveProgress({ step: successMessage, attempt: 3, maxAttempts: 3 });

      // Also update the JSON export
      const combinedOutput = { lesson: cleanedLesson, quizzes: quizzes.map(({ id, ...q }) => q) };
      setJsonOutput(JSON.stringify(combinedOutput, null, 2));
    } catch (error) {
      console.error("Error saving lesson:", error);
      const data = error.response?.data;
      const errMsg =
        (typeof data === "string" && data) ||
        data?.error ||
        data?.message ||
        error.message;
      setSaveProgress({ step: `Error: ${errMsg}`, attempt: 1, maxAttempts: 1 });
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Reset ────────────────────────────────────────────────────────────────
  const resetLesson = async () => {
    if (!originalLessonData) return;
    removeLessonDraft(lesson.sectionNumber);
    const loadId = beginSilentLoad();
    try {
      const existingLessonId = extractLessonIdFromImages(originalLessonData.lessonAnimation);
      setLesson({
        sectionNumber: originalLessonData.sectionNumber || "",
        sectionTitle: originalLessonData.sectionTitle || "",
        sectionImage: originalLessonData.sectionImage || "",
        topicHeaderNumber: originalLessonData.topicHeaderNumber || "",
        topicHeader: originalLessonData.topicHeader || "",
        topicNumber: originalLessonData.topicNumber || "",
        topicTitle: originalLessonData.topicTitle || "",
        lessonAnimation: originalLessonData.lessonAnimation || [],
        lessonText: originalLessonData.lessonText || "",
        ...(existingLessonId ? { lessonId: existingLessonId } : {}),
      });
      setLessonTextSource(originalLessonTextSource);
      if (originalLessonData.exampleId) {
        await loadLessonQuizzes(originalLessonData.sectionNumber);
      } else {
        setQuizzes([]);
        setOriginalQuizIds([]);
      }
      setDeletedQuizIds([]);
    } finally {
      finishSilentLoad(loadId);
    }
  };

  // ─── Build clean lesson for export ───────────────────────────────────────
  const buildCleanLessonForExport = () => {
    const cleanedLessonAnimation = (lesson.lessonAnimation || []).map((section) => {
      const cleanedSection = { ...section };
      if (section.subSections && section.subSections.length > 0) {
        cleanedSection.subSections = section.subSections.map((sub) => {
          const cleanedSub = { ...sub };
          delete cleanedSub.imageOptions;
          delete cleanedSub.selectedImageIndex;
          if (cleanedSub.miniSections) {
            cleanedSub.miniSections = cleanedSub.miniSections.map((mini) => {
              const m = { ...mini };
              delete m.imageOptions;
              delete m.selectedImageIndex;
              return m;
            });
          }
          return cleanedSub;
        });
      }
      return cleanedSection;
    });
    return { ...lesson, lessonAnimation: cleanedLessonAnimation };
  };

  // ─── Contextual lesson regeneration ───────────────────────────────────────
  const openRegenModal = () => {
    setRegenModal({ isOpen: true, lessonText: lesson.lessonText || "" });
  };

  const handleRegenerate = async () => {
    const { lessonText } = regenModal;
    if (!lessonText.trim()) {
      alert("Please enter source text for lesson regeneration.");
      return;
    }

    const sectionNumber = lesson.sectionNumber;
    setLesson((prev) => ({ ...prev, lessonText }));
    setHasUnsavedChanges(true);
    setRegenModal({ isOpen: false, lessonText: "" });

    // Mark as generating
    setRegeneratingLessons((prev) => {
      const next = new Map(prev);
      next.set(sectionNumber, { status: "generating" });
      return next;
    });

    // Fire and forget — non-blocking
    api
      .post("/api/regenerate-lesson-contextual", {
        lessonText,
        courseId: selectedCourse?._id,
        courseTitle: selectedCourse?.courseTitle || "",
        courseAuthor: selectedCourse?.courseAuthor || "",
        modelName: selectedCourse?.courseTopicsModelName,
        collectionName: selectedCourse?.courseTopicsCollectionName,
        sectionNumber,
        sectionTitle: lesson.sectionTitle,
        topicHeaderNumber: lesson.topicHeaderNumber,
        topicHeader: lesson.topicHeader,
        topicNumber: lesson.topicNumber,
        topicTitle: lesson.topicTitle,
        lessonId: lesson.lessonId,
        imageTheme,
      })
      .then((response) => {
        if (response.data.success) {
          setRegeneratingLessons((prev) => {
            const next = new Map(prev);
            next.set(sectionNumber, { status: "done", result: response.data.lessonAnimation });
            return next;
          });
          setRegenToast({
            sectionNumber,
            lessonAnimation: response.data.lessonAnimation,
            lessonId: response.data.lessonId,
          });
        }
      })
      .catch((err) => {
        console.error("Regeneration error:", err);
        setRegeneratingLessons((prev) => {
          const next = new Map(prev);
          next.set(sectionNumber, { status: "error" });
          return next;
        });
      });

    setIsRegenerating(false);
  };

  const applyRegeneratedLesson = (sectionNumber, lessonAnimation, lessonId) => {
    if (lesson.sectionNumber === sectionNumber) {
      setLesson((prev) => ({
        ...prev,
        ...(lessonId ? { lessonId } : {}),
        lessonAnimation: withAnimationIds(lessonAnimation),
      }));
      setHasUnsavedChanges(true);
    }
    setRegeneratingLessons((prev) => {
      const next = new Map(prev);
      next.delete(sectionNumber);
      return next;
    });
    setRegenToast(null);
  };

  // ─── Image generation ─────────────────────────────────────────────────────
  const templateHasImagePlaceholders = (template) => {
    if (!template || !Array.isArray(template.subSections)) return false;
    return template.subSections.some((sub) => {
      const hasSubImage = sub.className && sub.className.toLowerCase().includes("image");
      const hasMiniImage = sub.miniSections &&
        sub.miniSections.some((mini) => mini.className && mini.className.toLowerCase().includes("image"));
      return hasSubImage || hasMiniImage;
    });
  };

  const extractTemplateImageUrl = (template) => {
    if (!template || !Array.isArray(template.subSections)) return "";
    for (const sub of template.subSections) {
      if (sub.className && sub.className.toLowerCase().includes("image")) {
        if (sub.imageFilePath) return sub.imageFilePath;
        if (sub.imageOptions && sub.imageOptions.length > 0) {
          const idx = typeof sub.selectedImageIndex === "number" ? sub.selectedImageIndex : 0;
          return sub.imageOptions[idx] || sub.imageOptions[0];
        }
      }
      if (sub.miniSections) {
        for (const mini of sub.miniSections) {
          if (mini.className && mini.className.toLowerCase().includes("image")) {
            if (mini.imageFilePath) return mini.imageFilePath;
            if (mini.imageOptions && mini.imageOptions.length > 0) {
              const idx = typeof mini.selectedImageIndex === "number" ? mini.selectedImageIndex : 0;
              return mini.imageOptions[idx] || mini.imageOptions[0];
            }
          }
        }
      }
    }
    return "";
  };

  const handleSelectTemplateForSectionImage = (template) => {
    if (!template) return;
    if (selectedSectionImageTemplateId === template.id) {
      setSelectedSectionImageTemplateId(null);
      setLesson((prev) => ({ ...prev, sectionImage: "" }));
      return;
    }
    const imageUrl = extractTemplateImageUrl(template);
    setSelectedSectionImageTemplateId(template.id);
    setLesson((prev) => ({ ...prev, sectionImage: imageUrl || "" }));
  };

  const handleSectionImageUpdate = (templateId, imageUrl) => {
    if (!templateId || selectedSectionImageTemplateId !== templateId) return;
    setLesson((prev) => ({ ...prev, sectionImage: imageUrl || "" }));
  };

  const handleGenerateImages = async (specificSectionIndex = null) => {
    if (!lesson.lessonAnimation || lesson.lessonAnimation.length === 0) {
      setImageGenerationError("Please load a lesson first before generating images.");
      return;
    }
    if (!imageTheme || imageTheme.trim().length === 0) {
      setImageGenerationError("Please enter an image theme.");
      return;
    }
    const hasImages = lesson.lessonAnimation.some((section) =>
      section.subSections?.some((sub) =>
        (sub.className && sub.className.includes("image")) ||
        (sub.miniSections && sub.miniSections.some((mini) => mini.className && mini.className.includes("image")))
      )
    );
    if (!hasImages) {
      setImageGenerationError("No image placeholders found in the lesson.");
      return;
    }

    setIsGeneratingImages(true);
    setImageGenerationError(null);
    setImageGenerationProgress(
      specificSectionIndex !== null
        ? `Generating images for section ${specificSectionIndex + 1}…`
        : "Starting image generation…"
    );

    try {
      const lessonId = lesson.lessonId || nanoid();
      const response = await api.post("/api/generate-lesson-images", {
        lessonAnimation: lesson.lessonAnimation,
        lessonId,
        imageTheme,
        sectionIndex: specificSectionIndex,
        courseTitle: selectedCourse?.courseTitle,
      });

      if (response.data.success) {
        const updatedLessonAnimation = withAnimationIds(
          response.data.lessonAnimation
        );
        let nextSelectedTemplateId = selectedSectionImageTemplateId;
        let sectionImageOverride = "";
        let shouldOverrideSectionImage = false;

        if (selectedSectionImageTemplateId) {
          const selectedTemplate = updatedLessonAnimation.find(
            (t) => t.id === selectedSectionImageTemplateId
          );
          if (selectedTemplate) {
            sectionImageOverride = extractTemplateImageUrl(selectedTemplate) || "";
            shouldOverrideSectionImage = true;
          } else {
            nextSelectedTemplateId = null;
            sectionImageOverride = "";
            shouldOverrideSectionImage = true;
          }
        } else {
          const firstWithImages = updatedLessonAnimation.find(templateHasImagePlaceholders);
          if (firstWithImages) {
            nextSelectedTemplateId = firstWithImages.id;
            sectionImageOverride = extractTemplateImageUrl(firstWithImages) || "";
            shouldOverrideSectionImage = true;
          }
        }

        setLesson((prev) => ({
          ...prev,
          lessonId,
          lessonAnimation: updatedLessonAnimation,
          sectionImage: shouldOverrideSectionImage ? sectionImageOverride : prev.sectionImage,
        }));

        if (nextSelectedTemplateId !== selectedSectionImageTemplateId) {
          setSelectedSectionImageTemplateId(nextSelectedTemplateId);
        }

        setImageGenerationProgress(
          specificSectionIndex !== null
            ? `Images generated for section ${specificSectionIndex + 1}!`
            : `Generated ${response.data.generatedImages.length} image set(s)!`
        );
        setTimeout(() => setImageGenerationProgress(""), 3000);
      }
    } catch (error) {
      console.error("Error generating images:", error);
      setImageGenerationError(error.response?.data?.details || error.message || "Failed to generate images.");
    } finally {
      setIsGeneratingImages(false);
    }
  };

  // ─── Drag and drop ────────────────────────────────────────────────────────
  const onDragEnd = (result) => {
    const { destination, source } = result;
    if (!destination || destination.index === source.index) return;
    setLesson((prev) => {
      const newAnim = Array.from(prev.lessonAnimation);
      const [moved] = newAnim.splice(source.index, 1);
      newAnim.splice(destination.index, 0, moved);
      return { ...prev, lessonAnimation: newAnim };
    });
  };

  // ─── JSON copy (secondary export) ────────────────────────────────────────
  const handleCopyJson = async () => {
    const cleanedLesson = buildCleanLessonForExport();
    const cleanedQuizzes = quizzes.map(({ id, ...rest }) => rest);
    const combinedOutput = { lesson: cleanedLesson, quizzes: cleanedQuizzes };
    const json = JSON.stringify(combinedOutput, null, 2);

    const hasImageOptions = lesson.lessonAnimation.some((section) =>
      section.subSections?.some((sub) =>
        (sub.imageOptions && sub.imageOptions.length > 0) ||
        (sub.miniSections && sub.miniSections.some((mini) => mini.imageOptions && mini.imageOptions.length > 0))
      )
    );

    if (hasImageOptions) {
      const confirmed = window.confirm(
        "Copy JSON\n\nThis will also delete unused image options from AWS.\n\nProceed?"
      );
      if (!confirmed) return;

      await navigator.clipboard.writeText(json);
      setImageGenerationProgress("Cleaning up unused images from AWS…");

      try {
        const response = await api.post("/api/cleanup-unused-images", {
          lessonAnimation: lesson.lessonAnimation,
          lessonId: lesson.lessonId,
          courseTitle: selectedCourse?.courseTitle,
        });
        if (response.data.success) {
          setImageGenerationProgress(`JSON copied! Cleaned up ${response.data.deletedCount} unused images.`);
          setTimeout(() => setImageGenerationProgress(""), 5000);
        }
      } catch (err) {
        console.error("Error cleaning up images:", err);
      }
    } else {
      await navigator.clipboard.writeText(json);
      alert("JSON copied to clipboard!");
    }
  };

  // ─── Preview refresh ──────────────────────────────────────────────────────
  const refreshPreview = () => {
    setPreviewMode(false);
    setCurrentSectionIndex(-1);
    setTimeout(() => setPreviewMode(true), 100);
  };

  // ─── Notifications ────────────────────────────────────────────────────────
  const renderStickyNotification = () => {
    const processing = [];
    const success = [];
    const errors = [];

    if (isGeneratingImages) processing.push({ icon: "🎨", text: "Generating images… This may take a moment." });
    if (isRegenerating) processing.push({ icon: "🔄", text: "Regenerating lesson… You can navigate to other lessons." });
    if (imageGenerationProgress && !isGeneratingImages) success.push({ icon: "✅", text: imageGenerationProgress });
    if (saveProgress.step && !isSaving) success.push({ icon: "💾", text: saveProgress.step });
    if (imageGenerationError) errors.push({ icon: "⚠", text: imageGenerationError });

    if (processing.length === 0 && success.length === 0 && errors.length === 0) return null;

    const state = processing.length > 0 ? "processing" : errors.length > 0 ? "error" : "success";

    return (
      <div className={`sticky-notification ${state}`}>
        <div className="notification-content multi">
          {[processing, success, errors].map((group, gi) =>
            group.length > 0 && (
              <div className="notification-group" key={gi}>
                {group.map((msg, i) => (
                  <div className="notification-item" key={`${gi}-${i}`}>
                    <span className="notification-icon">{msg.icon}</span>
                    <span>{msg.text}</span>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    );
  };

  // ─── Regeneration toast ───────────────────────────────────────────────────
  const renderRegenToast = () => {
    if (!regenToast) return null;
    return (
      <div className="regen-toast">
        <span>Lesson {regenToast.sectionNumber} regenerated.</span>
        <button
          className="regen-toast-apply"
          onClick={() => applyRegeneratedLesson(regenToast.sectionNumber, regenToast.lessonAnimation, regenToast.lessonId)}
        >
          Apply
        </button>
        <button className="regen-toast-dismiss" onClick={() => setRegenToast(null)}>✕</button>
      </div>
    );
  };

  // ─── Regeneration modal ───────────────────────────────────────────────────
  const renderRegenModal = () => {
    if (!regenModal.isOpen) return null;

    const idx = allLessons.findIndex((l) => l.sectionNumber === lesson.sectionNumber);
    const prevLesson = idx > 0 ? allLessons[idx - 1] : null;
    const nextLesson = idx >= 0 && idx < allLessons.length - 1 ? allLessons[idx + 1] : null;

    return (
      <div className="modal-overlay" onClick={() => setRegenModal({ isOpen: false, lessonText: "" })}>
        <div className="modal-box modal-box-regen" onClick={(e) => e.stopPropagation()}>
          <h3 className="modal-title">Regenerate Lesson</h3>
          <p className="modal-subtitle">
            Regenerating: <strong>{lesson.sectionNumber} — {lesson.sectionTitle}</strong>
          </p>
          {prevLesson && (
            <p className="modal-context-hint">← Prev: {prevLesson.sectionTitle}</p>
          )}
          {nextLesson && (
            <p className="modal-context-hint">→ Next: {nextLesson.sectionTitle}</p>
          )}
          <div className="modal-label">Source text for this lesson</div>
          <textarea
            className="modal-textarea modal-textarea-regen"
            rows={22}
            placeholder="Paste the raw lesson text here…"
            value={regenModal.lessonText}
            onChange={(e) => setRegenModal((prev) => ({ ...prev, lessonText: e.target.value }))}
          />
          <p className="modal-note">
            Generation runs in background — you can navigate to other lessons while it runs.
          </p>
          <div className="modal-actions">
            <button
              className="reset-lesson-button"
              onClick={() => setRegenModal({ isOpen: false, lessonText: "" })}
            >
              Cancel
            </button>
            <button
              className="save-lesson-button"
              onClick={handleRegenerate}
              disabled={!regenModal.lessonText.trim()}
            >
              Regenerate
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── Mobile tab bar ───────────────────────────────────────────────────────
  const renderMobileTabBar = () => (
    <div className="mobile-tab-bar">
      {[
        { key: "overview", label: "Overview" },
        { key: "edit", label: "Edit" },
        { key: "chat", label: "AI Chat" },
        { key: "preview", label: "Preview" },
      ].map((tab) => (
        <button
          key={tab.key}
          className={`mobile-tab-item ${activeTab === tab.key ? "active" : ""}`}
          onClick={() => {
            if (tab.key === "overview") setIsOverviewOpen(true);
            else if (tab.key === "chat") setIsChatOpen(true);
            else setActiveTab(tab.key);
          }}
        >
          {tab.label}
          {tab.key === "chat" && hasUnsavedChanges && (
            <span className="unsaved-dot" />
          )}
        </button>
      ))}
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      {/* Regeneration modal */}
      {renderRegenModal()}

      {/* Regen toast */}
      {renderRegenToast()}

      {/* Panels */}
      <CourseOverviewPanel
        isOpen={isOverviewOpen}
        onClose={() => setIsOverviewOpen(false)}
        selectedCourse={selectedCourse}
        selectedLesson={lesson}
        onSelectLesson={handleSelectLessonFromOverview}
        onSelectWorkspace={handleSelectWorkspace}
        editorView={editorView}
        hasUnsavedChanges={hasUnsavedChanges}
        regeneratingLessons={regeneratingLessons}
        lessonDrafts={lessonDrafts}
        pendingCoverCount={coverOptions.length}
      />

      <CourseSettingsPanel
        isOpen={isCourseSettingsOpen}
        onClose={() => setIsCourseSettingsOpen(false)}
        selectedCourse={selectedCourse}
        onCourseUpdated={setSelectedCourse}
      />

      <AgentChatPanel
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        lesson={lesson}
        quizzes={quizzes}
        onLessonUpdate={handleAgentLessonUpdate}
        onQuizzesUpdate={handleAgentQuizzesUpdate}
        onRenumberApplied={handleRenumberApplied}
        onLabelRenameApplied={handleLabelRenameApplied}
        onDeleteApplied={handleDeleteApplied}
        onCourseEditConfirmed={runCourseEdit}
        onCourseEditStop={stopCourseEdit}
        hasUnsavedChanges={hasUnsavedChanges}
        selectedCourse={selectedCourse}
        allLessons={allLessons}
        imageTheme={imageTheme}
      />

      <div className="lesson-builder">
        {editorView === "lesson" && (
          <>
            <SidebarTemplatePreview
              templates={predefinedTemplates}
              onSelectTemplate={addTemplate}
            />
            <QuizSidebarTemplatePreview
              templates={predefinedQuizTemplates}
              onSelectTemplate={addQuiz}
            />
          </>
        )}

        {/* LEFT PANEL */}
        <div className={`left-side ${activeTab !== "edit" ? "mobile-hidden" : ""} ${editorView === "courseData" ? "is-course-chat" : ""}`}>
          {renderStickyNotification()}

          {/* Course selector + hamburger for lesson pick */}
          <div className="lessonSelectorContainer">
            {/* Toolbar row */}
            <div className="editor-toolbar">
              <button
                className="toolbar-btn overview-btn"
                onClick={() => setIsOverviewOpen(true)}
                title="Open course overview"
              >
                ☰ Course
              </button>
              <button
                className="toolbar-btn overview-btn"
                onClick={() => setIsCourseSettingsOpen(true)}
                title="Edit course details and cover"
                disabled={!selectedCourse}
              >
                ⚙ Settings
              </button>
              {hasUnsavedChanges && editorView === "lesson" && (
                <span className="unsaved-indicator">● Unsaved</span>
              )}
              {hasCourseDataUnsaved && editorView === "courseData" && (
                <span className="unsaved-indicator">● Unsaved</span>
              )}
              {editorView === "lesson" && (
                <button
                  className="toolbar-btn chat-btn"
                  onClick={() => setIsChatOpen(true)}
                  title="Open AI agent chat"
                >
                  ✦ AI Chat
                </button>
              )}
            </div>

            <CourseSelector setSelectedCourse={setSelectedCourse} />

            {editorView === "lesson" && isEditMode && (
              <LessonForm
                setLesson={setLesson}
                lesson={lesson}
                isEditMode={true}
              />
            )}
          </div>

          {editorView === "cover" && (
            <CourseCoverControls
              selectedCourse={selectedCourse}
              coverConcept={coverConcept}
              setCoverConcept={setCoverConcept}
              extraGuidance={coverExtraGuidance}
              setExtraGuidance={setCoverExtraGuidance}
              coverOptions={coverOptions}
              setCoverOptions={setCoverOptions}
              selectedCoverIndex={selectedCoverIndex}
              setSelectedCoverIndex={setSelectedCoverIndex}
              coverBusy={coverBusy}
              setCoverBusy={setCoverBusy}
              coverError={coverError}
              setCoverError={setCoverError}
              onCourseUpdated={(course) => {
                setSelectedCourse((prev) =>
                  prev ? { ...prev, ...course, courseTitle: prev.courseTitle } : course
                );
                setCoverCacheBust(Date.now());
                if (course?._id) loadPendingCovers(course._id);
              }}
              coverCacheBust={coverCacheBust}
            />
          )}

          {editorView === "courseData" && (
            <CourseDataAgent
              courseDraft={courseDraft}
              onDraftUpdate={handleCourseDraftUpdate}
              selectedCourse={selectedCourse}
            />
          )}

          {/* Save / Reset controls */}
          {editorView === "lesson" && isEditMode && (
            <div className="lesson-editor-controls">
              <button
                onClick={saveLesson}
                disabled={isSaving}
                className="save-lesson-button"
                data-tooltip="Writes this lesson and its quizzes to the database."
              >
                {isSaving ? "Saving…" : "Save to DB"}
              </button>
              <button
                onClick={resetLesson}
                disabled={isSaving}
                className="reset-lesson-button"
                data-tooltip="Discards unsaved edits and restores the last loaded version."
              >
                Reset
              </button>
              <button
                onClick={openRegenModal}
                disabled={isSaving}
                className="regen-lesson-button"
                data-tooltip="Rebuilds the lesson from source text. You can edit that text in the box that opens."
              >
                ↺ Regenerate
              </button>
              {regeneratingLessons.has(lesson.sectionNumber) && (
                <span className="regen-in-progress-badge">⟳ Generating…</span>
              )}
            </div>
          )}

          {editorView === "lesson" && isEditMode && (
            <div className="ai-lesson-generator-section">
              <div className="ai-lesson-generator-header">
                <h3>Lesson source text</h3>
              </div>
              {lessonTextSource === "import" && (
                <div className="lesson-source-stale">
                  This is the original import excerpt. It may be outdated if the
                  lesson was renumbered, merged, or regenerated.
                </div>
              )}
              <textarea
                className="ai-lesson-input lesson-source-text"
                placeholder="Source text this lesson was generated from…"
                value={lesson.lessonText || ""}
                readOnly
                rows={22}
              />
            </div>
          )}

          {/* AI Image Generator */}
          {editorView === "lesson" && isEditMode && lesson.lessonAnimation?.length > 0 && (
            <div className="ai-image-generator-section">
              <h3>AI Image Generator</h3>
              <textarea
                className="ai-image-theme-input"
                placeholder="Image theme…"
                value={imageTheme}
                onChange={(e) => setImageTheme(e.target.value)}
                rows={3}
                disabled={isGeneratingImages}
              />
              <button
                className="generate-images-button"
                onClick={() => handleGenerateImages(null)}
                disabled={isGeneratingImages || !imageTheme.trim()}
              >
                {isGeneratingImages ? "Generating Images…" : "Generate All Images"}
              </button>
              {imageGenerationProgress && (
                <div className="generation-progress">{imageGenerationProgress}</div>
              )}
              {imageGenerationError && (
                <div className="generation-error">{imageGenerationError}</div>
              )}
            </div>
          )}

          {/* Templates */}
          {editorView === "lesson" && isEditMode && lesson.lessonAnimation && (
            <div className="templates-container">
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="templates">
                  {(provided) => (
                    <div
                      className="droppable-area"
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {lesson.lessonAnimation.map((template, index) => (
                        <Draggable key={template.id} draggableId={template.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`template-item ${snapshot.isDragging ? "dragging" : ""}`}
                            >
                              {activeTemplateIndex === index ? (
                                <>
                                  <TemplateEditor
                                    key={`${template.id}-${template.name}`}
                                    template={template}
                                    sectionIndex={index}
                                    onDelete={() => deleteTemplate(index)}
                                    onSave={(updated) => updateTemplate(index, updated)}
                                    onDuplicate={(tpl) => duplicateTemplate(index, tpl)}
                                    onRegenerateImages={() => handleGenerateImages(index)}
                                    isGeneratingImages={isGeneratingImages}
                                    dragHandleProps={provided.dragHandleProps}
                                    isSelectedForSectionImage={selectedSectionImageTemplateId === template.id}
                                    onSelectForSectionImage={() => handleSelectTemplateForSectionImage(template)}
                                    onSectionImageUpdate={(url) => handleSectionImageUpdate(template.id, url)}
                                  />
                                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                                    <button className="save-lesson-button" onClick={() => setActiveTemplateIndex(null)}>Done</button>
                                  </div>
                                </>
                              ) : (
                                <div style={{ padding: 12, border: "1px solid #ddd", borderRadius: 8, background: "#fff" }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <div style={{ fontWeight: "bold" }}>{template.name || "Template"}</div>
                                    <div style={{ display: "flex", gap: 8 }}>
                                      <button className="reset-lesson-button" onClick={() => deleteTemplate(index)}>Delete</button>
                                      <button className="save-lesson-button" onClick={() => setActiveTemplateIndex(index)}>Edit</button>
                                    </div>
                                  </div>
                                  <div {...provided.dragHandleProps} style={{ marginTop: 8, opacity: 0.6, fontSize: 12 }}>
                                    ⠿ Drag
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          )}

          {/* Quizzes */}
          {editorView === "lesson" && isEditMode && (
            <div className="quizzes-container">
              <div className="quizzes-header">
                <h3>Lesson Quizzes</h3>
                <span className="quizzes-count-badge">
                  {quizzes.length} quiz{quizzes.length !== 1 ? "zes" : ""}
                </span>
              </div>

              {quizzes.length === 0 && (
                <div className="quizzes-empty">
                  No quizzes found for this lesson. Add one using the sidebar or ask the AI agent to generate quizzes.
                </div>
              )}

              <DragDropContext
                onDragEnd={(result) => {
                  if (!result.destination) return;
                  const items = Array.from(quizzes);
                  const [moved] = items.splice(result.source.index, 1);
                  items.splice(result.destination.index, 0, moved);
                  setQuizzes(items);
                }}
              >
                <Droppable droppableId="quizzes">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {quizzes.map((quiz, index) => (
                        <Draggable key={quiz.id} draggableId={quiz.id} index={index}>
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.draggableProps} className="quiz-item">
                              <QuizTemplateEditor
                                template={quiz}
                                sectionNumber={lesson.sectionNumber}
                                onDelete={() => deleteQuiz(index)}
                                onSave={(updatedQuiz) => updateQuiz(index, updatedQuiz)}
                                onDuplicate={(q) => duplicateQuiz(index, q)}
                                dragHandleProps={provided.dragHandleProps}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          )}

          {/* JSON output (secondary export) */}
          {editorView === "lesson" && jsonOutput && (
            <div style={{ width: "100%", marginTop: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontWeight: "bold" }}>JSON Export</div>
                <button className="save-lesson-button" onClick={handleCopyJson}>Copy JSON</button>
              </div>
              <textarea
                readOnly
                value={jsonOutput}
                style={{ width: "100%", minHeight: 300, fontFamily: "monospace", fontSize: 12, padding: 8, borderRadius: 8, border: "1px solid #ddd" }}
              />
            </div>
          )}
        </div>

        {/* RIGHT PANEL — Preview */}
        <div className={`right-side ${activeTab !== "preview" && activeTab !== "edit" ? "mobile-hidden" : ""} ${editorView === "courseData" ? "is-course-form" : ""}`}>
          {editorView === "cover" && (
            <CourseCoverPreview
              selectedCourse={selectedCourse}
              coverOptions={coverOptions}
              selectedCoverIndex={selectedCoverIndex}
              coverCacheBust={coverCacheBust}
            />
          )}
          {editorView === "courseData" && (
            <CourseDataPreview
              courseDraft={courseDraft}
              saving={courseDataSaving}
              saveError={courseDataSaveError}
              saveNotice={courseDataSaveNotice}
              onSave={handleSaveCourseData}
              onDraftUpdate={handleCourseDraftUpdate}
            />
          )}
          {editorView === "lesson" && isEditMode && (
            <>
              <div className="refresh-container">
                <button onClick={refreshPreview} className="refresh-preview-button">
                  <FontAwesomeIcon icon={faSyncAlt} />
                </button>
                <PreviewThemeToggle previewDark={previewDark} onChange={setPreviewDark} />
              </div>
              {previewMode && (
                <LessonPreview
                  lesson={lesson}
                  currentSectionIndex={currentSectionIndex}
                  setCurrentSectionIndex={setCurrentSectionIndex}
                  quizzes={quizzes}
                  previewDark={previewDark}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile tab bar */}
      {renderMobileTabBar()}
    </>
  );
};

export default LessonEditor;
